import boundary from './baihedongBoundary.json';
import supplement from './baihedongBoundarySupplement.json';
import './tiandituBaihedongBoundary.scss';

const BOUNDARY_EVENTS = ['move', 'zoom', 'moveend', 'zoomend', 'resize'];
const SVG_NS = 'http://www.w3.org/2000/svg';
// GeoJSONcn 声明为 WGS84；仅用于区域展示，未按官方行政区划图校准。
// 标签高 56px，底边与主区域最北侧保持 20px 间距，缩放时仍位于区域外。
const LABEL_TOP_OFFSET = -76;

function getBounds (points) {
  return {
    left: Math.min(...points.map(point => point.x)),
    right: Math.max(...points.map(point => point.x)),
    top: Math.min(...points.map(point => point.y)),
    bottom: Math.max(...points.map(point => point.y))
  };
}

// 独立管理白鹤洞边界、名称、总览取景和资源清理；身份判定由主地图提供。
export default {
  data () {
    return {
      baihedongOverviewLabel: null,
      baihedongOverviewArea: null,
      baihedongBoundaryRings: null,
      baihedongFrame: null
    };
  },
  methods: {
    bindBaihedongBoundaryEvents () {
      // 拖动、缩放结束及尺寸变化均更新投影，更新过程不改变镜头。
      BOUNDARY_EVENTS.forEach(eventName => {
        this.bindMapEvent(this.map, eventName, this.scheduleBaihedongUpdate);
      });
    },
    getBaihedongBoundaryRings () {
      if (!this.baihedongBoundaryRings) {
        // 主区域保留下载顶点，金道社区按图片近似补绘；各 Polygon 独立闭合，避免跨空隙连线。
        const rings = [...boundary.features, ...supplement.features].flatMap(feature => feature.geometry.coordinates);
        this.baihedongBoundaryRings = rings.map(ring =>
          ring.map(([lng, lat]) => this.getMapPoint({ lng, lat, coordinateSystem: 'WGS84' })));
      }
      return this.baihedongBoundaryRings;
    },
    // 只有进入总览时调整镜头，日常图层更新不改变用户视野。
    showBaihedongOverview (minZoom) {
      const map = this.map;
      const api = window.T;
      const points = this.getBaihedongBoundaryRings().flat();
      const viewport = map.getViewport(points);
      // reset 同步更新视野，再以完整轮廓测量可用空间，取代旧的一公里圆形估算。
      map.centerAndZoom(viewport.center, viewport.zoom, { reset: true });
      const size = map.getSize();
      const bounds = getBounds(points.map(point => map.lngLatToContainerPoint(point)));
      const ratio = Math.min(size.x * 0.54 / (bounds.right - bounds.left), size.y * 0.56 / (bounds.bottom - bounds.top));
      const zoom = Math.max(minZoom, Math.min(17, map.getZoom() + Math.floor(Math.log2(ratio))));
      // 白鹤洞场景以实际总览取景级别作为区域高亮和自动名称的共同门槛。
      this.labelOverviewZoom = zoom;
      map.centerAndZoom(viewport.center, zoom, { reset: true });
      // 让展示中心下移到画面高度的 60%，为顶部统计栏留出空间。
      const viewportCenter = map.containerPointToLngLat(new api.Point(size.x / 2, size.y * 0.4));
      map.centerAndZoom(viewportCenter, zoom, { reset: true });
      this.updateBaihedongOverview();
      this.hasManualViewport = false;
    },
    createBaihedongOverview () {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('aria-hidden', 'true');
      svg.classList.add('tianditu-baihedong-overview-area');
      // 相同地理路径分层绘制浅填充、柔光和细边线，缩放时线宽保持屏幕像素大小。
      ['fill', 'glow', 'outline'].forEach(name => {
        const path = document.createElementNS(SVG_NS, 'path');
        path.classList.add(`tianditu-baihedong-boundary-${name}`);
        path.setAttribute('fill-rule', 'evenodd');
        svg.appendChild(path);
      });
      // 区域高亮只叠加到底图，不遮挡单位、名称和业务面板，不拦截地图操作。
      this.map.getPanes().tilePane.appendChild(svg);
      this.baihedongOverviewArea = svg;
      const label = new window.T.Label({
        text: '白鹤洞街道',
        position: this.getBaihedongLabelPoint(),
        offset: this.getTiandituLabelOffset(-104, LABEL_TOP_OFFSET),
        className: 'tianditu-baihedong-overview-label'
      });
      this.map.addLayer(label);
      this.baihedongOverviewLabel = label;
    },
    getBaihedongLabelPoint () {
      // 锚点取主区域外包框的北侧中点，独立小块不影响街道名称的位置。
      const points = this.getBaihedongBoundaryRings()[0];
      return new window.T.LngLat(
        (Math.min(...points.map(point => point.lng)) + Math.max(...points.map(point => point.lng))) / 2,
        Math.max(...points.map(point => point.lat))
      );
    },
    scheduleBaihedongUpdate () {
      if (!this.isMapReady() || this._isBeingDestroyed || this.baihedongFrame !== null) return;
      this.baihedongFrame = requestAnimationFrame(() => {
        this.baihedongFrame = null;
        this.updateBaihedongOverview();
      });
    },
    // 根据真实视野更新位置和大小，移出范围后隐藏，移回来自动恢复。
    updateBaihedongOverview () {
      if (!this.isMapReady()) return;
      if (!this.isBaihedongOverview()) {
        this.clearBaihedongOverview();
        return;
      }
      if (!this.baihedongOverviewArea) this.createBaihedongOverview();
      const map = this.map;
      const size = map.getSize();
      const rings = this.getBaihedongBoundaryRings().map(ring =>
        ring.map(point => map.lngLatToContainerPoint(point)));
      const bounds = getBounds(rings.flat());
      const visible = this.isLabelOverviewVisible() && bounds.right >= 0 && bounds.left <= size.x &&
        bounds.bottom >= 0 && bounds.top <= size.y;
      const svg = this.baihedongOverviewArea;
      svg.style.display = visible ? '' : 'none';
      if (visible) {
        const origin = map.lngLatToLayerPoint(map.containerPointToLngLat(new window.T.Point(0, 0)));
        svg.style.left = `${origin.x}px`;
        svg.style.top = `${origin.y}px`;
        svg.setAttribute('width', size.x);
        svg.setAttribute('height', size.y);
        svg.setAttribute('viewBox', `0 0 ${size.x} ${size.y}`);
        svg.classList.toggle('is-detail', this.currentDw !== null);
        const path = rings.map(ring => ring.map((point, index) =>
          `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ') + ' Z').join(' ');
        svg.querySelectorAll('path').forEach(element => element.setAttribute('d', path));
      }
      const labelPoint = map.lngLatToContainerPoint(this.getBaihedongLabelPoint());
      // 名称只在锚点可见且未选中单位时展示，详情期间保留边线并减弱填充。
      this.baihedongOverviewLabel.getElement().style.display = visible && this.currentDw === null &&
        labelPoint.x >= 104 && labelPoint.x <= size.x - 104 &&
        labelPoint.y >= -LABEL_TOP_OFFSET && labelPoint.y <= size.y ? '' : 'none';
    },
    clearBaihedongOverview () {
      if (this.baihedongOverviewArea) this.baihedongOverviewArea.remove();
      this.baihedongOverviewArea = null;
      if (this.map && this.baihedongOverviewLabel) {
        this.map.removeLayer(this.baihedongOverviewLabel);
      }
      this.baihedongOverviewLabel = null;
      this.baihedongBoundaryRings = null;
    }
  },
  beforeDestroy () {
    if (this.baihedongFrame !== null) cancelAnimationFrame(this.baihedongFrame);
    this.baihedongFrame = null;
    if (this.map) {
      BOUNDARY_EVENTS.forEach(eventName => this.map.removeEventListener(eventName, this.scheduleBaihedongUpdate));
    }
    this.clearBaihedongOverview();
  }
};
